import React, { useState } from "react";
import {
  Search,
  Eye,
  Phone,
  Mail,
  User,
  MessageSquare,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";

const ThereachAddress = () => {
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Sample data for processed real estate inquiries
const [requests] = useState(
[]
);


  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case "in-progress":
        return <Clock className="w-4 h-4 text-blue-400" />;
      case "contacted":
        return <Phone className="w-4 h-4 text-orange-400" />;
      case "cancelled":
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-500/20 text-green-400";
      case "in-progress":
        return "bg-blue-500/20 text-blue-400";
      case "contacted":
        return "bg-orange-500/20 text-orange-400";
      case "cancelled":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-slate-500/20 text-slate-400";
    }
  };

  const filteredRequests = requests.filter((request) => {
    const matchesSearch =
      request.name.toLowerCase().includes(search.toLowerCase()) ||
      request.email.toLowerCase().includes(search.toLowerCase()) ||
      request.phone.includes(search);
    const matchesStatus =
      statusFilter === "all" || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const MessageModal = ({ request, onClose }) => {
    if (!request) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4">
          <div className="p-4 lg:p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg lg:text-xl font-semibold text-white">
                Request Details
              </h3>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-300 text-2xl p-1"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Name
                  </label>
                  <p className="text-white text-sm lg:text-base break-words">{request.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Phone
                  </label>
                  <p className="text-white text-sm lg:text-base break-all">{request.phone}</p>
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Email
                  </label>
                  <p className="text-white text-sm lg:text-base break-all">{request.email}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Message
                </label>
                <div className="bg-slate-700/50 p-3 lg:p-4 rounded-lg">
                  <p className="text-white leading-relaxed text-sm lg:text-base">
                    {request.message}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Status
                  </label>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(request.status)}
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        request.status
                      )}`}
                    >
                      {request.status.replace("-", " ").toUpperCase()}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Processed Date
                  </label>
                  <p className="text-white text-sm lg:text-base">
                    {new Date(request.processedDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
          All New Requests From The Reach Address
        </h1>
        <p className="text-slate-400 text-sm lg:text-base">
          View and manage completed real estate inquiries
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 lg:w-5 lg:h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 lg:pl-10 pr-4 py-2 lg:py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-white placeholder-slate-400 text-sm lg:text-base"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 lg:px-4 py-2 lg:py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-white text-sm lg:text-base"
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="in-progress">In Progress</option>
          <option value="contacted">Contacted</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Requests Grid */}
      <div className="grid gap-4">
        {filteredRequests.map((request) => (
          <div
            key={request.id}
            className="bg-slate-800/50 backdrop-blur border border-slate-700/50 hover:border-blue-500/50 rounded-lg p-4 lg:p-6 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-200"
          >
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 lg:w-5 lg:h-5 text-slate-400" />
                    <h3 className="text-base lg:text-lg font-semibold text-white">
                      {request.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(request.status)}
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        request.status
                      )}`}
                    >
                      {request.status.replace("-", " ").toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 mb-4">
                  <div className="flex items-center gap-2 text-slate-400 text-sm lg:text-base">
                    <Phone className="w-3 h-3 lg:w-4 lg:h-4" />
                    <span className="break-all">{request.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 text-sm lg:text-base">
                    <Mail className="w-3 h-3 lg:w-4 lg:h-4" />
                    <span className="break-all">{request.email}</span>
                  </div>
                </div>

                <div className="flex items-start gap-2 mb-4">
                  <MessageSquare className="w-3 h-3 lg:w-4 lg:h-4 text-slate-400 mt-1 flex-shrink-0" />
                  <p className="text-slate-300 line-clamp-2 text-sm lg:text-base">
                    {request.message}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between gap-2 text-xs lg:text-sm text-slate-400">
                  <span>
                    Received: {new Date(request.date).toLocaleDateString()}
                  </span>
                  <span>
                    Processed:{" "}
                    {new Date(request.processedDate).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setSelectedRequest(request)}
                className="flex items-center justify-center gap-2 px-3 lg:px-4 py-2 lg:py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg shadow-blue-500/50 text-sm lg:text-base w-full lg:w-auto lg:ml-4"
              >
                <Eye className="w-3 h-3 lg:w-4 lg:h-4" />
                <span className="lg:inline">View Details</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <div className="text-slate-400 mb-2">
            <MessageSquare className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-slate-500">No requests found</p>
        </div>
      )}

      {/* Message Modal */}
      <MessageModal
        request={selectedRequest}
        onClose={() => setSelectedRequest(null)}
      />
    </div>
  );
};

export default ThereachAddress;